import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getSavedSellers,
  addSavedSeller,
  removeSavedSeller,
  refreshSavedSeller,
  SavedSeller,
} from '../api/sellers'
import { track } from '../utils/track'
import { useToast } from '../context/ToastContext'
import { Button, IconButton, SearchField, Badge } from '../components/common'
import styles from './PageStyles.module.css'
import sellerStyles from './SellersPage.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function ProgressBar({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ flex: 1, height: '4px', background: 'var(--stonks-border)', borderRadius: '2px', overflow: 'hidden', minWidth: '60px' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--stonks-accent-primary)', transition: 'width 0.5s ease' }} />
      </div>
      <span style={{ fontSize: '0.7rem', color: 'var(--stonks-text-muted)', whiteSpace: 'nowrap' }}>
        {pct}%
      </span>
    </div>
  )
}

function SellerStatus({ seller }: { seller: SavedSeller }) {
  if (seller.status === 'error') {
    return (
      <Badge tone="error" dot title={seller.error ?? undefined} style={{ cursor: 'help' }}>
        Error
      </Badge>
    )
  }
  if (seller.status === 'pending') {
    const p = seller.progress
    if (!p || p.totalPages === 0) {
      return <Badge tone="neutral" dot>Connecting…</Badge>
    }
    return (
      <div style={{ minWidth: '120px' }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--stonks-text-muted)', marginBottom: '3px' }}>
          Pass {p.pass}/{p.totalPasses} · pg {p.page}/{p.totalPages}
        </div>
        <ProgressBar value={p.page} total={p.totalPages} />
      </div>
    )
  }
  // ready
  const e = seller.enrichment
  if (e?.running) {
    return (
      <div style={{ minWidth: '120px' }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--stonks-text-muted)', marginBottom: '3px' }}>
          Enriching {e.done}/{e.total}
        </div>
        <ProgressBar value={e.done} total={e.total} />
      </div>
    )
  }
  return <Badge tone="success" dot>Ready</Badge>
}

export function SellersPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [input, setInput] = useState('')

  const needsPolling = (sellers: SavedSeller[]) =>
    sellers.some(s => s.status === 'pending' || s.enrichment?.running)

  const { data: sellers = [], isLoading } = useQuery<SavedSeller[]>({
    queryKey: ['saved-sellers'],
    queryFn: getSavedSellers,
    refetchInterval: (query) => needsPolling(query.state.data ?? []) ? 2000 : false,
  })

  const addMutation = useMutation({
    mutationFn: addSavedSeller,
    onSuccess: () => {
      track('seller_scan', { username: input.trim() })
      queryClient.invalidateQueries({ queryKey: ['saved-sellers'] })
      queryClient.invalidateQueries({ queryKey: ['saved-seller', input.trim()] })
      setInput('')
      showToast('Seller added — fetching listings in background', 'success')
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  })

  const removeMutation = useMutation({
    mutationFn: removeSavedSeller,
    onSuccess: (_, username) => {
      queryClient.invalidateQueries({ queryKey: ['saved-sellers'] })
      queryClient.invalidateQueries({ queryKey: ['saved-seller', username] })
      showToast('Seller removed', 'info')
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  })

  const refreshMutation = useMutation({
    mutationFn: refreshSavedSeller,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-sellers'] })
      showToast('Re-fetching listings in background', 'info')
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    const u = input.trim()
    if (u) addMutation.mutate(u)
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>Sellers</h1>
      </div>

      {/* Add seller form */}
      <div className={releaseStyles.searchFilterRow} data-tour="sellers-scan">
        <form onSubmit={handleAdd} className={sellerStyles.addForm}>
          <div data-tour="sellers-input">
          <SearchField
            value={input}
            onChange={setInput}
            placeholder="Discogs username…"
            icon="bi bi-person-badge"
          />
          </div>
          <Button type="submit" variant="primary" icon="bi bi-plus-lg" disabled={!input.trim() || addMutation.isPending}>
            Add
          </Button>
        </form>
      </div>

      {/* Sellers table */}
      <div className={styles.gridView}>
        {isLoading ? (
          <div className={styles.empty}><i className="bi bi-hourglass"></i><p>Loading…</p></div>
        ) : sellers.length === 0 ? (
          <div className={styles.empty}>
            <i className="bi bi-shop"></i>
            <p>No saved sellers yet</p>
            <p className={styles.emptyHint}>Add a Discogs seller username above to track their inventory</p>
          </div>
        ) : (
          <table className={`${styles.table} ${sellerStyles.sellersTable}`}>
            <thead>
              <tr>
                <th>Seller</th>
                <th style={{ textAlign: 'right' }}>Listings</th>
                <th style={{ textAlign: 'right' }}>Unique releases</th>
                <th>Last updated</th>
                <th>Status</th>
                <th style={{ width: '120px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sellers.map(seller => (
                <tr
                  key={seller.username}
                  style={{ cursor: seller.status === 'ready' ? 'pointer' : undefined }}
                  onClick={() => seller.status === 'ready' && navigate(`/seller/${seller.username}`)}
                >
                  <td className={sellerStyles.nameCell}>
                    <span style={{ fontWeight: 500 }}>{seller.username}</span>
                  </td>
                  <td data-label="Listings" style={{ textAlign: 'right', color: 'var(--stonks-text-muted)' }}>
                    {seller.total_listings > 0 ? seller.total_listings.toLocaleString() : '—'}
                  </td>
                  <td data-label="Unique releases" style={{ textAlign: 'right', color: 'var(--stonks-text-muted)' }}>
                    {seller.unique_releases > 0 ? seller.unique_releases.toLocaleString() : '—'}
                  </td>
                  <td data-label="Last updated" style={{ color: 'var(--stonks-text-muted)', fontSize: '0.8rem' }}>
                    {formatDate(seller.last_fetched_at)}
                  </td>
                  <td data-label="Status"><SellerStatus seller={seller} /></td>
                  <td className={sellerStyles.actionsCell} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      {seller.status === 'ready' && (
                        <IconButton
                          icon="bi bi-eye"
                          aria-label="View listings"
                          title="View listings"
                          onClick={() => navigate(`/seller/${seller.username}`)}
                        />
                      )}
                      <IconButton
                        icon="bi bi-arrow-clockwise"
                        aria-label="Refresh listings"
                        title="Refresh listings"
                        onClick={() => refreshMutation.mutate(seller.username)}
                        disabled={seller.status === 'pending' || refreshMutation.isPending}
                      />
                      <IconButton
                        icon="bi bi-trash"
                        aria-label="Remove seller"
                        title="Remove seller"
                        variant="danger"
                        onClick={() => removeMutation.mutate(seller.username)}
                        disabled={removeMutation.isPending}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
